import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { CalendarIcon, Search, MapPin, Users, ArrowRight } from "lucide-react";
import PropertyCard from "@/components/PropertyCard";

const HERO_IMG = "https://images.unsplash.com/photo-1718275521201-1b8066ea2669?w=2400&q=85";

export default function Home() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [all, setAll] = useState([]);
  const [wishlistIds, setWishlistIds] = useState(new Set());

  // Search state
  const [location, setLocation] = useState("");
  const [checkIn, setCheckIn] = useState();
  const [checkOut, setCheckOut] = useState();
  const [guests, setGuests] = useState("2");

  useEffect(() => {
    api.get("/properties?featured=true").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/properties").then((r) => setAll(r.data)).catch(() => {});
    api.get("/wishlist").then((r) => setWishlistIds(new Set(r.data.property_ids || []))).catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (location) params.set("location", location);
    if (checkIn) params.set("check_in", format(checkIn, "yyyy-MM-dd"));
    if (checkOut) params.set("check_out", format(checkOut, "yyyy-MM-dd"));
    if (guests) params.set("guests", guests);
    navigate(`/search?${params.toString()}`);
  };

  const handleWishlistChange = (id, inList) => {
    setWishlistIds((prev) => {
      const next = new Set(prev);
      if (inList) next.add(id); else next.delete(id);
      return next;
    });
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 scrim-top" />
          <div className="absolute inset-0 bg-black/25" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 pt-16 md:pt-32 pb-24 md:pb-44">
          <p className="text-white/85 text-xs sm:text-sm uppercase tracking-[0.25em] mb-4 fade-up">Curated stays · Goa · Bangalore</p>
          <h1 className="font-serif-display text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.08] max-w-3xl fade-up">
            Unhurried mornings.<br/>
            <em className="font-normal">Beautifully hosted homes.</em>
          </h1>
          <p className="mt-5 text-white/85 max-w-xl text-sm sm:text-base lg:text-lg leading-relaxed fade-up">
            A small collection of owner-operated villas and guest houses — chosen for the way they make you feel, not for how they photograph.
          </p>
        </div>

        {/* Search widget */}
        <div className="relative -mt-12 md:-mt-20 mx-auto max-w-6xl px-4 md:px-10">
          <form
            onSubmit={onSearch}
            className="bg-white rounded-md shadow-[0_20px_60px_rgba(0,0,0,0.1)] p-4 md:p-6 grid grid-cols-1 md:grid-cols-5 gap-3 md:gap-4"
            data-testid="hero-search-form"
          >
            <div className="md:col-span-1">
              <label className="text-[10px] uppercase tracking-widest text-[hsl(100_3%_30%)]">Destination</label>
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger className="mt-1 border-0 border-b border-[hsl(165_37%_17%/0.15)] rounded-none px-0 h-11 focus:ring-0" data-testid="search-location">
                  <MapPin className="h-4 w-4 mr-2 text-[hsl(100_3%_30%)]" />
                  <SelectValue placeholder="Anywhere" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Goa">Goa</SelectItem>
                  <SelectItem value="Bangalore">Bangalore</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-1">
              <label className="text-[10px] uppercase tracking-widest text-[hsl(100_3%_30%)]">Check in</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="mt-1 w-full h-11 border-b border-[hsl(165_37%_17%/0.15)] flex items-center gap-2 text-left"
                    data-testid="search-checkin"
                  >
                    <CalendarIcon className="h-4 w-4 text-[hsl(100_3%_30%)]" />
                    <span className={checkIn ? "" : "text-[hsl(100_3%_30%)]"}>
                      {checkIn ? format(checkIn, "MMM d, yyyy") : "Select date"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 bg-white">
                  <Calendar mode="single" selected={checkIn} onSelect={setCheckIn} disabled={{ before: new Date() }} />
                </PopoverContent>
              </Popover>
            </div>

            <div className="md:col-span-1">
              <label className="text-[10px] uppercase tracking-widest text-[hsl(100_3%_30%)]">Check out</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="mt-1 w-full h-11 border-b border-[hsl(165_37%_17%/0.15)] flex items-center gap-2 text-left"
                    data-testid="search-checkout"
                  >
                    <CalendarIcon className="h-4 w-4 text-[hsl(100_3%_30%)]" />
                    <span className={checkOut ? "" : "text-[hsl(100_3%_30%)]"}>
                      {checkOut ? format(checkOut, "MMM d, yyyy") : "Select date"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 bg-white">
                  <Calendar mode="single" selected={checkOut} onSelect={setCheckOut} disabled={{ before: checkIn || new Date() }} />
                </PopoverContent>
              </Popover>
            </div>

            <div className="md:col-span-1">
              <label className="text-[10px] uppercase tracking-widest text-[hsl(100_3%_30%)]">Guests</label>
              <div className="mt-1 h-11 border-b border-[hsl(165_37%_17%/0.15)] flex items-center gap-2">
                <Users className="h-4 w-4 text-[hsl(100_3%_30%)]" />
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  className="border-0 focus-visible:ring-0 px-0 h-9"
                  data-testid="search-guests"
                />
              </div>
            </div>

            <div className="md:col-span-1 flex items-end">
              <Button type="submit" className="w-full btn-terracotta h-12 rounded-full" data-testid="search-submit">
                <Search className="h-4 w-4 mr-2" /> Search stays
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Signature collection */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 mt-16 md:mt-28" data-testid="featured-section">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[hsl(10_51%_55%)] mb-3">The Signature Collection</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl">
              Homes chosen for the way they make you feel.
            </h2>
          </div>
          <Link to="/search" className="hidden md:inline-flex items-center gap-2 text-sm font-medium hover:text-[hsl(10_51%_55%)]" data-testid="view-all-featured">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="featured-grid">
          {featured.slice(0, 6).map((p) => (
            <PropertyCard key={p.id} property={p} isWishlisted={wishlistIds.has(p.id)} onWishlistChange={handleWishlistChange} />
          ))}
        </div>
      </section>

      {/* Destinations */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 mt-16 md:mt-28" data-testid="destinations-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { name: "Goa", tag: "Coastal · Slow · Sun", img: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=1600&q=80", link: "/search?location=Goa" },
            { name: "Bangalore", tag: "Urban · Green · Cosmopolitan", img: "https://images.unsplash.com/photo-1596436889106-be35e843f974?w=1600&q=80", link: "/search?location=Bangalore" },
          ].map((d) => (
            <Link key={d.name} to={d.link} className="relative group block card-image rounded-md" data-testid={`destination-${d.name.toLowerCase()}`}>
              <div className="aspect-[16/12] sm:aspect-[16/10] overflow-hidden rounded-md">
                <img src={d.img} alt={d.name} className="w-full h-full object-cover" />
              </div>
              <div className="absolute inset-0 scrim-bottom rounded-md" />
              <div className="absolute bottom-0 left-0 p-8">
                <p className="text-white/80 text-xs uppercase tracking-[0.2em]">{d.tag}</p>
                <h3 className="font-serif-display text-white text-4xl mt-2">{d.name}</h3>
                <p className="text-white/85 text-sm mt-2 inline-flex items-center gap-2">Explore stays <ArrowRight className="h-4 w-4" /></p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* All properties */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 mt-16 md:mt-28" data-testid="all-properties-section">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.25em] text-[hsl(10_51%_55%)] mb-3">Browse the collection</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight">Every home, hand-picked.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {all.slice(0, 9).map((p) => (
            <PropertyCard key={p.id} property={p} isWishlisted={wishlistIds.has(p.id)} onWishlistChange={handleWishlistChange} />
          ))}
        </div>
      </section>

      {/* Editorial */}
      <section className="mx-auto max-w-5xl px-6 md:px-10 mt-16 md:mt-28 text-center" data-testid="promise-section">
        <p className="text-xs uppercase tracking-[0.25em] text-[hsl(10_51%_55%)] mb-4">Our promise</p>
        <p className="font-serif-display text-3xl md:text-4xl leading-relaxed">
          “We only list homes we would happily stay in ourselves — vetted for craft, comfort, and warm human hospitality.”
        </p>
        <p className="mt-6 text-sm text-[hsl(100_3%_30%)]">— NewDayStays hosts</p>
      </section>
    </div>
  );
}
